/**
 * A FILE FROM A ROOM IS HELD AS A TYPE THAT CANNOT RUN (security audit, 2026-11-05).
 *
 * The uploader names a file's type, S3 serves it, and `res.blob()` keeps it — so a Blob of `image/svg+xml` or
 * `text/html` became an object URL that, opened as a page (a tab, an <object>), ran its script as app.avengents.ai.
 * The chat service now stores and serves only inert types (chat/lambda/attachments.ts storedType); this is the same
 * rule on the bytes the app holds, so a file uploaded before that, or any other door, lands inert too:
 *   - a raster picture, a film, a sound and a PDF keep their type (each element shows it without running anything);
 *   - text and plain data become `text/plain` (the viewer reads the words, nothing renders them);
 *   - everything else — html, svg, xml, javascript, unknown — becomes `application/octet-stream`.
 */
const RASTER = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif', 'image/bmp', 'image/heic', 'image/heif',
])
const TEXT = new Set(['text/plain', 'text/markdown', 'text/csv', 'application/json'])

function inertType(type: string): string {
  const t = (type.split(';')[0] ?? '').trim().toLowerCase()
  if (RASTER.has(t) || t === 'application/pdf') return t
  if (/^(audio|video)\/[a-z0-9.+-]+$/.test(t)) return t
  if (TEXT.has(t)) return 'text/plain'
  return 'application/octet-stream'
}

/** The same bytes, retyped when their type is not one that renders inertly. */
export function inertBlob(blob: Blob): Blob {
  const t = inertType(blob.type)
  return t === blob.type ? blob : new Blob([blob], { type: t })
}
